import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Loader2, AlertTriangle, Shield, TrendingUp, Users, Heart,
  Sparkles, BarChart3, Activity, Moon, FileText, Video,
  BookOpen, Mic, RefreshCw, ChevronDown, ChevronUp, Target, Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// PROACTIVE ALERTS
// ═══════════════════════════════════════════════════════
function ProactiveAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [scanInfo, setScanInfo] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/intelligence/proactive-alerts`)
      .then(r => r.json()).then(d => {
        setScanInfo(d);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/intelligence/proactive-scan`, { method: 'POST' });
      const data = await res.json();
      setAlerts(data.alerts || []);
      setAiAnalysis(data.ai_analysis || '');
      setScanInfo(data.scan);
    } catch {}
    setScanning(false);
  };

  const severityStyles = {
    critical: { bg: 'bg-terra-50', border: 'border-l-terra-400', badge: 'bg-terra-400 text-white', icon: AlertTriangle },
    high: { bg: 'bg-terra-50', border: 'border-l-terra-300', badge: 'bg-terra-200 text-terra-500', icon: AlertTriangle },
    moderate: { bg: 'bg-sky-50', border: 'border-l-sky-300', badge: 'bg-sky-100 text-sky-500', icon: Activity },
    low: { bg: 'bg-sage-50', border: 'border-l-sage-300', badge: 'bg-sage-100 text-sage-500', icon: Shield },
  };

  return (
    <div data-testid="proactive-alerts">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-terra-50 flex items-center justify-center">
            <Shield className="w-5 h-5 text-terra-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Proactive AI Alerts</h2>
            <p className="text-xs text-[#5C6A5F]">Clinical risks detected before anyone asks</p>
          </div>
        </div>
        <Button onClick={runScan} disabled={scanning} className="rounded-full bg-terra-400 hover:bg-terra-500 text-white"
          data-testid="proactive-scan-btn">
          {scanning ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Scanning...</>
            : <><RefreshCw className="w-4 h-4 mr-1.5" />Run Proactive Scan</>}
        </Button>
      </div>

      {scanInfo && scanInfo.alerts_found > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-sand-50 rounded-xl p-3 border border-sand-200 text-center">
            <p className="text-xl font-heading font-bold text-[#2C3D30]">{scanInfo.scanned}</p>
            <p className="text-xs text-[#5C6A5F]">Scanned</p>
          </div>
          <div className="bg-terra-50 rounded-xl p-3 border border-terra-100 text-center">
            <p className="text-xl font-heading font-bold text-terra-400">{scanInfo.critical || 0}</p>
            <p className="text-xs text-terra-400">Critical</p>
          </div>
          <div className="bg-terra-50 rounded-xl p-3 border border-terra-100 text-center">
            <p className="text-xl font-heading font-bold text-terra-300">{scanInfo.high || 0}</p>
            <p className="text-xs text-terra-300">High</p>
          </div>
          <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 text-center">
            <p className="text-xl font-heading font-bold text-sky-500">{(scanInfo.alerts_found || 0) - (scanInfo.critical || 0) - (scanInfo.high || 0)}</p>
            <p className="text-xs text-sky-500">Other</p>
          </div>
        </div>
      )}

      {aiAnalysis && (
        <div className="mb-4 p-4 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-terra-400" data-testid="proactive-ai-analysis">
          <p className="text-xs font-medium text-terra-400 mb-2 uppercase tracking-wide">AI Risk Assessment</p>
          <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{aiAnalysis}</p>
        </div>
      )}

      {alerts.length > 0 ? (
        <div className="space-y-2" data-testid="proactive-alert-list">
          {alerts.map(alert => {
            const style = severityStyles[alert.severity] || severityStyles.low;
            const Icon = style.icon;
            return (
              <div key={alert.id} className={`p-4 rounded-2xl ${style.bg} border-l-4 ${style.border} border border-sand-200`}
                data-testid={`alert-${alert.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-terra-400" />
                    <span className="text-sm font-heading font-semibold text-[#2C3D30]">{alert.patient_name}</span>
                    <Badge className={`${style.badge} rounded-full text-xs`}>{alert.severity}</Badge>
                  </div>
                  <span className="text-xs text-[#5C6A5F]">{alert.assigned_to}</span>
                </div>
                <p className="text-xs text-[#5C6A5F] mb-1">{alert.diagnosis} &middot; Pain {alert.pain_level}/10 &middot; PPS {alert.pps_score} &middot; {alert.level_of_care}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {alert.flags.map((flag, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-white/60 text-[#2C3D30] border border-sand-200">{flag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : !scanning && (
        <div className="text-center py-12 text-[#5C6A5F]">
          <Shield className="w-10 h-10 mx-auto mb-3 text-sand-300" />
          <p className="text-sm">{scanInfo?.message || 'Click "Run Proactive Scan" to analyze all patients for clinical risks.'}</p>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// EXECUTIVE COCKPIT
// ═══════════════════════════════════════════════════════
function ExecutiveCockpit() {
  const [data, setData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/intelligence/executive-cockpit`)
      .then(r => r.json()).then(setData)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const generateBrief = async () => {
    setBriefing(true);
    try {
      const res = await fetch(`${API}/api/intelligence/executive-cockpit/ai-brief`);
      setBrief(await res.json());
    } catch {}
    setBriefing(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-sage-400" /></div>;

  return (
    <div data-testid="executive-cockpit">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sage-50 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-sage-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Executive Cockpit</h2>
            <p className="text-xs text-[#5C6A5F]">Unified platform metrics at a glance</p>
          </div>
        </div>
        <Button onClick={generateBrief} disabled={briefing} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white"
          data-testid="exec-brief-btn">
          {briefing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Generating...</>
            : <><Sparkles className="w-4 h-4 mr-1.5" />AI Daily Brief</>}
        </Button>
      </div>

      {brief && (
        <div className="mb-5 p-5 rounded-2xl bg-sage-50 border border-sage-100 border-l-4 border-l-sage-400" data-testid="exec-brief">
          <p className="text-xs font-medium text-sage-600 mb-2 uppercase tracking-wide">Today's Executive Brief</p>
          <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{brief.brief}</p>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* Census Row */}
          <div>
            <p className="text-xs font-medium text-[#5C6A5F] mb-2 uppercase tracking-wide">Census & Clinical</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {[
                { label: 'Active', value: data.census.active, color: 'sage' },
                { label: 'GIP', value: data.census.gip, color: 'sky' },
                { label: 'Crisis', value: data.census.crisis, color: 'terra' },
                { label: 'High Pain', value: data.census.high_pain, color: 'terra' },
                { label: 'Avg Pain', value: data.census.avg_pain, color: 'sky' },
                { label: 'Avg PPS', value: data.census.avg_pps, color: 'sage' },
                { label: 'Declining', value: data.census.declining, color: 'terra' },
              ].map((m, i) => (
                <div key={i} className={`bg-${m.color}-50 rounded-xl p-3 border border-${m.color}-100 text-center`}>
                  <p className="text-xl font-heading font-bold text-[#2C3D30]">{m.value}</p>
                  <p className="text-xs text-[#5C6A5F]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Adoption Row */}
          <div>
            <p className="text-xs font-medium text-[#5C6A5F] mb-2 uppercase tracking-wide">AI Platform Adoption</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Total AI Uses', value: data.ai_adoption.total_ai_interactions, icon: Sparkles },
                { label: 'Ambient Notes', value: data.ai_adoption.ambient_notes, icon: Mic },
                { label: 'Approved Notes', value: data.ai_adoption.ambient_approved, icon: FileText },
                { label: 'Goodnight Checks', value: data.ai_adoption.goodnight_checks, icon: Moon },
                { label: 'Family Triage', value: data.ai_adoption.is_normal_queries, icon: Heart },
                { label: 'NL Queries', value: data.ai_adoption.nl_queries, icon: Target },
              ].map((m, i) => (
                <div key={i} className="bg-sand-50 rounded-xl p-3 border border-sand-200 text-center">
                  <m.icon className="w-4 h-4 mx-auto mb-1 text-sage-400" />
                  <p className="text-lg font-heading font-bold text-[#2C3D30]">{m.value}</p>
                  <p className="text-xs text-[#5C6A5F]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Family Engagement */}
          <div>
            <p className="text-xs font-medium text-[#5C6A5F] mb-2 uppercase tracking-wide">Family Engagement</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Portal Users', value: data.family_engagement.family_users, icon: Users },
                { label: 'Memories', value: data.family_engagement.memories_captured, icon: BookOpen },
                { label: 'Life Stories', value: data.family_engagement.life_stories, icon: Sparkles },
                { label: 'Evening Updates', value: data.family_engagement.goodnight_checks_sent, icon: Moon },
                { label: 'Video Visits', value: data.family_engagement.video_visits, icon: Video },
                { label: 'Bereavement', value: data.family_engagement.bereavement_journeys, icon: Heart },
              ].map((m, i) => (
                <div key={i} className="bg-sand-50 rounded-xl p-3 border border-sand-200 text-center">
                  <m.icon className="w-4 h-4 mx-auto mb-1 text-sky-300" />
                  <p className="text-lg font-heading font-bold text-[#2C3D30]">{m.value}</p>
                  <p className="text-xs text-[#5C6A5F]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quality */}
          <div>
            <p className="text-xs font-medium text-[#5C6A5F] mb-2 uppercase tracking-wide">Quality & Compliance</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-sand-50 rounded-xl p-4 border border-sand-200 text-center">
                <p className="text-3xl font-heading font-bold text-[#2C3D30]">{data.quality.avg_documentation_score || '—'}<span className="text-base text-[#5C6A5F]">/100</span></p>
                <p className="text-xs text-[#5C6A5F] mt-1">Avg Documentation Quality</p>
              </div>
              <div className="bg-sand-50 rounded-xl p-4 border border-sand-200 text-center">
                <p className="text-3xl font-heading font-bold text-[#2C3D30]">{data.quality.survey_readiness}<span className="text-base text-[#5C6A5F]">%</span></p>
                <p className="text-xs text-[#5C6A5F] mt-1">Survey Readiness</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MAIN PAGE WITH REAL-TIME NOTIFICATION BADGES
// ═══════════════════════════════════════════════════════
export default function IntelligenceDashboardPage() {
  const [activeTab, setActiveTab] = useState('cockpit');
  const [alertCount, setAlertCount] = useState({ critical: 0, high: 0, total: 0 });
  const [hasNew, setHasNew] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  // Poll for new alerts every 30 seconds
  useEffect(() => {
    const checkAlerts = async () => {
      try {
        const res = await fetch(`${API}/api/intelligence/proactive-alerts`);
        const data = await res.json();
        if (data.critical || data.high) {
          const newCritical = data.critical || 0;
          const newHigh = data.high || 0;
          const newTotal = data.alerts_found || 0;

          if (lastCheck && (newCritical > alertCount.critical || newTotal > alertCount.total)) {
            setHasNew(true);
          }

          setAlertCount({ critical: newCritical, high: newHigh, total: newTotal });
          setLastCheck(Date.now());
        }
      } catch {}
    };

    checkAlerts();
    const interval = setInterval(checkAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'cockpit', label: 'Executive Cockpit', icon: BarChart3, color: 'sage' },
    { id: 'alerts', label: 'Proactive Alerts', icon: Shield, color: 'terra' },
  ];

  const tabColors = {
    sage: 'bg-sage-50 text-sage-400 border border-sage-200',
    terra: 'bg-terra-50 text-terra-400 border border-terra-200',
  };

  return (
    <div className="min-h-screen bg-sand-100" data-testid="intelligence-dashboard-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">Command Intelligence</h1>
            {alertCount.critical > 0 && (
              <div className="relative" data-testid="critical-alert-badge">
                <div className="flex items-center gap-1.5 bg-terra-400 text-white rounded-full px-3 py-1 text-xs font-medium animate-pulse">
                  <Bell className="w-3 h-3" />
                  {alertCount.critical} Critical
                </div>
                {hasNew && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-terra-500 rounded-full border-2 border-sand-100 animate-ping" />
                )}
              </div>
            )}
            {alertCount.high > 0 && (
              <div className="flex items-center gap-1.5 bg-terra-200 text-terra-500 rounded-full px-3 py-1 text-xs font-medium" data-testid="high-alert-badge">
                {alertCount.high} High
              </div>
            )}
          </div>
          <p className="text-sm text-[#5C6A5F] mt-1">Proactive clinical alerts and unified executive metrics</p>
        </div>

        <div className="flex gap-2 mb-6 sm:mb-8 pb-4 border-b border-sand-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'alerts') setHasNew(false);
            }}
              className={`relative flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id ? tabColors[tab.color] : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-200'
              }`} data-testid={`intel-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.id === 'cockpit' ? 'Cockpit' : 'Alerts'}</span>
              {tab.id === 'alerts' && alertCount.total > 0 && (
                <span className={`ml-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                  alertCount.critical > 0 ? 'bg-terra-400 text-white' : 'bg-sand-300 text-[#5C6A5F]'
                }`} data-testid="alert-count-badge">
                  {alertCount.total}
                </span>
              )}
              {tab.id === 'alerts' && hasNew && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-terra-400 rounded-full animate-ping" />
              )}
            </button>
          ))}
        </div>

        <div className="bg-sand-50 rounded-2xl border border-sand-200 p-4 sm:p-6 lg:p-8">
          {activeTab === 'cockpit' && <ExecutiveCockpit />}
          {activeTab === 'alerts' && <ProactiveAlerts />}
        </div>
      </div>
    </div>
  );
}
